import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { AllocationService } from '../../services/allocation.service';
import { AllocationCreditType, AllocationTargetType } from '../../interfaces/allocation.interface';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';

export interface AllocationTargetOption {
  id: string;
  label: string;
  outstanding: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onAllocated: () => void;
  creditType: AllocationCreditType;
  creditId: string;
  /** Unapplied balance of the credit. */
  available: number;
  targetType: AllocationTargetType;
  targets: AllocationTargetOption[];
  title?: string;
  targetLabel?: string;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * W3-E: partial allocation of an unapplied credit (credit note, advance, debit note) to one open document.
 * The amount defaults to the most both sides allow and may be lowered; the rest stays unapplied.
 */
const AllocateDialog: React.FC<Props> = ({
  open, onClose, onAllocated, creditType, creditId, available, targetType, targets,
  title = 'Apply to document', targetLabel = 'Open document',
}) => {
  const toast = useToast();
  const [targetId, setTargetId] = useState('');
  const [amount, setAmount] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const selected = useMemo(() => targets.find(t => t.id === targetId), [targets, targetId]);
  const cap = selected ? Math.min(available, selected.outstanding) : available;

  useEffect(() => {
    if (open) { setTargetId(''); setAmount(''); }
  }, [open]);

  const handleTarget = (id: string) => {
    setTargetId(id);
    const t = targets.find(x => x.id === id);
    setAmount(t ? round2(Math.min(available, t.outstanding)) : '');
  };

  const tooBig = amount !== '' && Number(amount) > cap + 0.005;
  const invalid = !targetId || amount === '' || Number(amount) <= 0 || tooBig;

  const handleApply = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      await AllocationService.allocate({ creditType, creditId, targetType, targetId, amount: Number(amount) });
      toast.showSuccess('Allocation recorded');
      onAllocated();
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to allocate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="allocation.apply.page" open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            Unapplied balance: {formatMoney(available)}. You can apply part of it now and the rest later.
          </Typography>
          <SearchableSelect<AllocationTargetOption>
            data-help="allocation.apply.field.target"
            label={targetLabel}
            required
            options={targets}
            value={targetId}
            onChange={handleTarget}
            getOptionLabel={t => `${t.label} - outstanding ${formatMoney(t.outstanding)}`}
            noOptionsText="Nothing open to apply to"
          />
          <TextField
            data-help="allocation.apply.field.amount"
            label="Amount to apply"
            type="number"
            required
            value={amount}
            onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            error={tooBig}
            helperText={tooBig ? `At most ${formatMoney(cap)} can be applied here` : selected ? `Up to ${formatMoney(cap)}` : undefined}
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-help="allocation.apply.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button data-help="allocation.apply.confirm" variant="contained" onClick={handleApply} disabled={saving || invalid}>
          {saving ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AllocateDialog;
